// src/components/common/SeasonContext.jsx
import { createContext, useContext, useState, useEffect } from "react";

/** Sports season starts on 1 June (month index 5 in JS Date). */
export const SPORTS_SEASON_START_MONTH = 5;
export const FIRST_SEASON_START_YEAR = 2018;

export function getSeasonStartYear(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  return referenceDate.getMonth() >= SPORTS_SEASON_START_MONTH ? year : year - 1;
}

export function formatSeasonLabel(startYear) {
  return `${startYear}-${startYear + 1}`;
}

export function getCurrentSeasonLabel(referenceDate = new Date()) {
  return formatSeasonLabel(getSeasonStartYear(referenceDate));
}

export function parseSeasonStartYear(seasonLabel) {
  if (!seasonLabel || typeof seasonLabel !== "string") return null;
  const match = seasonLabel.match(/^(\d{4})-(\d{4})$/);
  if (!match) return null;
  const startYear = Number(match[1]);
  const endYear = Number(match[2]);
  if (!Number.isFinite(startYear) || endYear !== startYear + 1) return null;
  return startYear;
}

export function getSeasonRange(seasonLabel) {
  const startYear = parseSeasonStartYear(seasonLabel);
  if (startYear === null) {
    const fallbackYear = getSeasonStartYear();
    return {
      start: new Date(fallbackYear, SPORTS_SEASON_START_MONTH, 1),
      end: new Date(fallbackYear + 1, SPORTS_SEASON_START_MONTH - 1, 31),
    };
  }

  return {
    start: new Date(startYear, SPORTS_SEASON_START_MONTH, 1),
    end: new Date(startYear + 1, SPORTS_SEASON_START_MONTH - 1, 31),
  };
}

export function buildSeasonOptions({
  firstSeasonStartYear = FIRST_SEASON_START_YEAR,
  referenceDate = new Date(),
} = {}) {
  const latestStartYear = getSeasonStartYear(referenceDate);
  const length = Math.max(1, latestStartYear - firstSeasonStartYear + 1);

  return Array.from({ length }, (_, index) => {
    const startYear = latestStartYear - index;
    return formatSeasonLabel(startYear);
  });
}

function seasonStorageKey(clubId) {
  return clubId ? `season:${clubId}` : "season";
}

function readStoredSeason(clubId) {
  if (typeof window === "undefined") return null;
  try {
    const keyed = window.localStorage.getItem(seasonStorageKey(clubId));
    if (parseSeasonStartYear(keyed) !== null) return keyed;
    // Legacy global key (pre per-club namespacing).
    const legacy = window.localStorage.getItem("season");
    return parseSeasonStartYear(legacy) !== null ? legacy : null;
  } catch {
    return null;
  }
}

function resolveInitialSeason(clubId) {
  const current = getCurrentSeasonLabel();
  const stored = readStoredSeason(clubId);
  if (!stored) return current;

  const storedStartYear = parseSeasonStartYear(stored);
  const currentStartYear = parseSeasonStartYear(current);
  if (storedStartYear === null || currentStartYear === null) return current;

  // Keep an explicit user choice for past/current seasons; never select a future season.
  if (storedStartYear <= currentStartYear) return stored;
  return current;
}

const SeasonContext = createContext();

export function clearStoredSeasons(clubId = null) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("season");
    if (clubId) window.localStorage.removeItem(seasonStorageKey(clubId));
  } catch {
    /* ignore storage failures */
  }
}

export function SeasonProvider({ children, clubId = null }) {
  const [season, setSeason] = useState(() => resolveInitialSeason(clubId));

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(seasonStorageKey(clubId), season);
    } catch {
      /* ignore storage failures */
    }
  }, [season, clubId]);

  return (
    <SeasonContext.Provider value={{ season, setSeason }}>
      {children}
    </SeasonContext.Provider>
  );
}

export const useSeason = () => useContext(SeasonContext);
